import React, { useEffect, useMemo, useState } from "react";
import { useActivePage } from '../../../contexts/ActivePageContext';
import AutoSum from '../../autoSum';
import AutoFix from '../../autoFix';
import secureLocalStorage from 'react-secure-storage';
import styles from './Edit.module.css';
import ReactModal from 'react-modal';
import { Button, ButtonHalf } from '../../../theme/button';
import { useAuth } from '../../../contexts/AuthContext';

//Icons
import { ReactComponent as SaveIcon } from "../../../assets/icons/svg/content-save-outline.svg";
import { ReactComponent as CancelIcon } from "../../../assets/icons/svg/cancel.svg";
import { ReactComponent as AutoFixIcon } from "../../../assets/icons/svg/auto-fix.svg";
import { ReactComponent as CounterIcon } from "../../../assets/icons/svg/counter.svg";
import { ReactComponent as UndoIcon } from "../../../assets/icons/svg/arrow-u-left-top.svg";
import { ReactComponent as RedoIcon } from "../../../assets/icons/svg/arrow-u-right-top.svg";
import { ReactComponent as ColBeforeIcon } from "../../../assets/icons/svg/table-column-plus-before.svg";
import { ReactComponent as ColAfterIcon } from "../../../assets/icons/svg/table-column-plus-after.svg";
import { ReactComponent as ColRemoveIcon } from "../../../assets/icons/svg/table-column-remove.svg";
import { ReactComponent as RowBeforeIcon } from "../../../assets/icons/svg/table-row-plus-before.svg";
import { ReactComponent as RowAfterIcon } from "../../../assets/icons/svg/table-row-plus-after.svg";
import { ReactComponent as RowRemoveIcon } from "../../../assets/icons/svg/table-row-remove.svg";

function safeParse(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  } catch {
    return fallback;
  }
}

export default function TableEdit() {
  const { setActivePage } = useActivePage();
  const { getValidToken } = useAuth();
  const [jsonOutputVar] = useState(() => safeParse("jsonOutput", []));
  const [headersVar] = useState(() => safeParse("headers", {}));
  const [mergedDataVar] = useState(() => safeParse("mergedData", { data: [] }));
  const [headerRowCountVar] = useState(() => safeParse("headerRowCount", 1));
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const [tableData, setTableData] = useState(() => jsonOutputVar || []);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedCellText, setSelectedCellText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [HashedIDParam] = useState(() => safeParse("HashedID", ""));
  const [mergeLog, setMergeLog] = useState(false);

  // Undo/redo stacks
  const [history, setHistory] = useState([tableData[0]?.data || []]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Modal states
  const [isAutoFixVisible, setAutoFixVisible] = useState(false);
  const [isAutoSumVisible, setAutoSumVisible] = useState(false);

  useEffect(() => {
    setTableData(Array.isArray(jsonOutputVar) ? jsonOutputVar : []);
  }, [jsonOutputVar]);

  const currentTableData = useMemo(() => {
    if (!Array.isArray(tableData) || tableData.length === 0) {
      return { table: "Untitled", data: [[]] };
    }
    return tableData[0];
  }, [tableData]);

  const [tableName, setTableName] = useState(() => currentTableData.table?.toString?.() || "Untitled");

  useEffect(() => {
    setTableName(currentTableData.table?.toString?.() || "Untitled");
  }, [currentTableData]);

  // ---- Cell handlers ----
  const handleCellClick = (rowIndex, cellIndex, cell) => {
    setSelectedCell({ rowIndex, cellIndex });
    setSelectedCellText(cell);
    setIsEditing(true);
  };

  const handleCellChange = (e) => setSelectedCellText(e.target.value);

  const saveCellChange = () => {
    if (!selectedCell) return;
    const updatedData = currentTableData.data.map((row, r) =>
      row.map((c, cIndex) => (r === selectedCell.rowIndex && cIndex === selectedCell.cellIndex ? selectedCellText : c))
    );
    updateTableWithHistory(updatedData);
    setIsEditing(false);
  };

  // ---- Table ops ----
  const updateTable = (updatedData) => {
    const updatedTableData = [...tableData];
    updatedTableData[0].data = updatedData;
    setTableData(updatedTableData);
  };

  const updateTableWithHistory = (newData) => {
    setHistory((prevHistory) => {
      const newHistory = prevHistory.slice(0, historyIndex + 1); // Trim forward history
      return [...newHistory, newData];
    });
    setHistoryIndex((prevIndex) => prevIndex + 1);
    updateTable(newData);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const previousState = history[historyIndex - 1];
      setHistoryIndex((prev) => prev - 1);
      updateTable(previousState);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setHistoryIndex((prev) => prev + 1);
      updateTable(nextState);
    }
  };

  const addRowBefore = () => {
    if (selectedCell) {
      const newRow = Array(currentTableData.data[0].length).fill("");
      const updatedData = [...currentTableData.data];
      updatedData.splice(selectedCell.rowIndex, 0, newRow);
      updateTableWithHistory(updatedData);
    }
  };

  const addRowAfter = () => {
    if (selectedCell) {
      const newRow = Array(currentTableData.data[0].length).fill("");
      const updatedData = [...currentTableData.data];
      updatedData.splice(selectedCell.rowIndex + 1, 0, newRow);
      updateTableWithHistory(updatedData);
    }
  };

  const removeRow = () => {
    if (selectedCell) {
      const updatedData = [...currentTableData.data];
      updatedData.splice(selectedCell.rowIndex, 1);
      updateTableWithHistory(updatedData);

      if (selectedCell.rowIndex > 0) {
        setSelectedCell({
          rowIndex: selectedCell.rowIndex - 1,
          cellIndex: selectedCell.cellIndex,
        });
      } else {
        setSelectedCell(null);
      }
    }
  };

  const addColBefore = () => {
    if (selectedCell) {
      const updatedData = currentTableData.data.map((row) => {
        const newRow = [...row];
        newRow.splice(selectedCell.cellIndex, 0, "");
        return newRow;
      });
      updateTableWithHistory(updatedData);
    }
  };

  const addColAfter = () => {
    if (selectedCell) {
      const updatedData = currentTableData.data.map((row) => {
        const newRow = [...row];
        newRow.splice(selectedCell.cellIndex + 1, 0, "");
        return newRow;
      });
      updateTableWithHistory(updatedData);
    }
  };

  const removeCol = () => {
    if (selectedCell) {
      const updatedData = currentTableData.data.map((row) => {
        const newRow = [...row];
        newRow.splice(selectedCell.cellIndex, 1);
        return newRow;
      });
      updateTableWithHistory(updatedData);

      if (selectedCell.cellIndex > 0) {
        setSelectedCell({
          rowIndex: selectedCell.rowIndex,
          cellIndex: selectedCell.cellIndex - 1,
        });
      } else {
        setSelectedCell(null);
      }
    }
  };

  // ---- Save to API (web equivalent) ----
  const handleSaveTable = async () => {
    setIsLoading(true);
    setIsEditing(false);

    const { table, data } = currentTableData;
    const columns = data.length > 0 ? data[0].length : 0;
    const rows = data.length;

    try {
      const validToken = await getValidToken();
      const response = await fetch("https://ngyk2k01bl.execute-api.us-east-1.amazonaws.com/prod/updateLogbook", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${validToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          hashedID: HashedIDParam,
          data: data,
          columns: columns.toString(),
          rows: rows.toString(),
          name: tableName.toString()
        })
      });
      const responseData = await response.json();
    } catch (error) {
      console.error("Error updating logbook:", error);
    }

    localStorage.setItem("HashedID", "");
    setIsLoading(false);
    setActivePage("logbooks");
  };

  // ---- Cancel Edit ----
  const cancelEdit = () => {
    setIsEditing(false);
    setSelectedCell(null);
    setSelectedCellText("");
    localStorage.setItem("HashedID", "");
    setActivePage("logbooks");
  };

  // ---- Advanced Feature Implementations ----
  const runAutoFix = async () => {
    if (!currentTableData) return;

    try {
      const fixed = await AutoFix(currentTableData); 


      if (fixed?.data) {
        updateTableWithHistory(fixed.data);
      } else {
        console.warn("AutoFix returned an unexpected shape:", fixed);
      }
    } catch (error) {
      console.error("AutoFix failed:", error);
    } finally {
      setAutoFixVisible(false);
    }
  };

  const runAutoSum = () => {
    if (!currentTableData) return;
    const updated = AutoSum(currentTableData); 
    updateTableWithHistory(updated.data);     
    setAutoSumVisible(false);  
  };

  const handleKeyNavigation = (e, rowIndex, cellIndex) => {
    if (!currentTableData?.data) return;
    const numRows = currentTableData.data.length;
    const numCols = currentTableData.data[0].length;

    let newRow = rowIndex;
    let newCell = cellIndex;

    switch (e.key) {
      case "Tab":
        e.preventDefault();
        if (e.shiftKey) {
          // Move left
          newCell = cellIndex > 0 ? cellIndex - 1 : numCols - 1;
          newRow = cellIndex > 0 ? rowIndex : rowIndex > 0 ? rowIndex - 1 : numRows - 1;
        } else {
          // Move right
          newCell = cellIndex < numCols - 1 ? cellIndex + 1 : 0;
          newRow = cellIndex < numCols - 1 ? rowIndex : rowIndex < numRows - 1 ? rowIndex + 1 : 0;
        }
        break;

      case "Enter":
        e.preventDefault();
        newRow = rowIndex < numRows - 1 ? rowIndex + 1 : 0;
        newCell = cellIndex;
        break;

      case "ArrowRight":
        newCell = cellIndex < numCols - 1 ? cellIndex + 1 : cellIndex;
        break;
      case "ArrowLeft":
        newCell = cellIndex > 0 ? cellIndex - 1 : cellIndex;
        break;
      case "ArrowDown":
        newRow = rowIndex < numRows - 1 ? rowIndex + 1 : rowIndex;
        break;
      case "ArrowUp":
        newRow = rowIndex > 0 ? rowIndex - 1 : rowIndex;
        break;
      default:
        return; // ignore other keys
    }

    // Save current cell first
    saveCellChange();

    // Move to new cell
    const nextCellValue = currentTableData.data[newRow][newCell];
    setSelectedCell({ rowIndex: newRow, cellIndex: newCell });
    setSelectedCellText(nextCellValue);
    setIsEditing(true);
  };

  return (
    <div className={styles.container}>
      {/* Table Title */}
      <div className={styles.tableNameContainer}>
        {isEditingTitle ? (
          <input
            className={styles.tableNameInput}
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            onBlur={() => setIsEditingTitle(false)}
            onKeyDown={(e) => e.key === "Enter" && setIsEditingTitle(false)}
            autoFocus
          />
        ) : (
          <h1
            className={styles.tableNameInput}
            onClick={() => setIsEditingTitle(true)}
          >
            {tableName}
          </h1>
        )}
      </div>
        <div className={styles.actionBar}>
          {/* Save */}
          <button
            className={styles.actionButton}
            onClick={handleSaveTable}
            disabled={isLoading}
          >
            <SaveIcon className={`${styles.icon} ${styles.iconSave}`} />
            <span>{isLoading ? "Saving..." : "Save"}</span>
          </button>

          {/* Cancel */}
          <button
            className={styles.actionButton}
            onClick={cancelEdit}
          >
            <CancelIcon className={`${styles.icon} ${styles.iconCancel}`} />
            <span>Cancel</span>
          </button>

          {/* Auto Fix */}
          <button className={styles.actionButton} onClick={() => setAutoFixVisible(true)}>
            <AutoFixIcon className={`${styles.icon} ${styles.iconTertiary}`} />
            <span>Auto Fix</span>
          </button>

          {/* Auto Sum */}
          <button className={styles.actionButton} onClick={() => setAutoSumVisible(true)}>
            <CounterIcon className={`${styles.icon} ${styles.iconTertiary}`} />
            <span>Auto Sum</span>
          </button>

          {/* Undo */}
          <button
            className={styles.actionButton}
            onClick={handleUndo}
            disabled={historyIndex <= 0}
          >
            <UndoIcon className={`${styles.icon} ${styles.iconPrimary}`} />
            <span>Undo</span>
          </button>

          {/* Redo */}
          <button
            className={styles.actionButton}
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
          >
            <RedoIcon className={`${styles.icon} ${styles.iconPrimary}`} />
            <span>Redo</span>
          </button>

          {/* Row/Col actions (disabled if no cell selected) */}
          <button className={styles.actionButton} onClick={addColBefore} >
            <ColBeforeIcon className={`${styles.icon} ${styles.iconDark}`} />
            <span>Add Column Left</span>
          </button>

          <button className={styles.actionButton} onClick={addColAfter} >
            <ColAfterIcon className={`${styles.icon} ${styles.iconDark}`} />
            <span>Add Column Right</span>
          </button>

          <button className={styles.actionButton} onClick={removeCol}>
            <ColRemoveIcon className={`${styles.icon} ${styles.iconDark}`} />
            <span>Delete Column</span>
          </button>

          <button className={styles.actionButton} onClick={addRowBefore} >
            <RowBeforeIcon className={`${styles.icon} ${styles.iconDark}`} />
            <span>Insert Row Above</span>
          </button>

          <button className={styles.actionButton} onClick={addRowAfter} >
            <RowAfterIcon className={`${styles.icon} ${styles.iconDark}`} />
            <span>Insert Row Below</span>
          </button>

          <button className={styles.actionButton} onClick={removeRow} >
            <RowRemoveIcon className={`${styles.icon} ${styles.iconDark}`} />
            <span>Delete Row</span>
          </button>
        </div>
      

      {/* Table */}
      <div className={styles.tableWrapper}>
      <div className={styles.table}>
        {currentTableData.data.map((row, rowIndex) => (
          <div key={rowIndex} className={styles.row}>
            {row.map((cell, cellIndex) => {
              const isSelected =
                selectedCell?.rowIndex === rowIndex &&
                selectedCell?.cellIndex === cellIndex;
              return (
                <div
                  key={cellIndex}
                  className={`${styles.cell} ${isSelected ? styles.cellSelected : ""}`}
                  onClick={() => handleCellClick(rowIndex, cellIndex, cell)}
                >
                  {isSelected && isEditing ? (
                    <input
                      className={styles.cellInput}
                      value={selectedCellText}
                      onChange={handleCellChange}
                      onBlur={saveCellChange}
                      onKeyDown={(e) => handleKeyNavigation(e, rowIndex, cellIndex)}
                      autoFocus
                    />
                  ) : (
                    <span className={styles.cellText}>{cell}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      </div>

      {/* AutoFix Modal */}
      <ReactModal
        isOpen={isAutoFixVisible}
        onRequestClose={() => setAutoFixVisible(false)}
        contentLabel="AutoFix Modal"
        className={styles.modalContent}
        overlayClassName={styles.modalOverlay}
      >
        <h2>Auto Fix</h2>
        <p>Auto Fix uses the headers (1st row) and automatically fixes things like date into a standard format, fixes IATA airport codes, and fixes airplane names.  </p>
        <div className={styles.modalActions}>
          <Button onClick={runAutoFix}>Run AutoFix</Button>
          <Button className={styles.cancelButton} onClick={() => setAutoFixVisible(false)}> Cancel </Button>
        </div>
      </ReactModal>

      {/* AutoSum Modal */}
      <ReactModal
        isOpen={isAutoSumVisible}
        onRequestClose={() => setAutoSumVisible(false)}
        contentLabel="AutoSum Modal"
        className={styles.modalContent}
        overlayClassName={styles.modalOverlay}
      >
        <h2>Auto Sum</h2>
        <p>Auto Sum takes the numeric columns and creates a sum row at the bottom with totals. This button makes assumptions about the columns and the column headers.</p>
        <p>*For the best results, run the AutoFix fucntion to correct the data first.</p>
        <div className={styles.modalActions}>
          <Button onClick={runAutoSum}>Run AutoSum</Button>
          <Button className={styles.cancelButton} onClick={() => setAutoSumVisible(false)}> Cancel </Button>
        </div>
      </ReactModal>
    </div>
  );
}
